<template>
  <div class="papas-merge-card">
    <div class="questions-details">

      <div class="title-container" v-if="knownCardNumber">
        <div class="title">בשביל שהתזרים יהיה מדויק, צריך פירוט של הכרטיס:</div>
        <div class="account">{{ formattedCardNumber }}</div>
        <img class="logo" v-if="hasSource" :src="require(`@/assets/bank-logos-30/${logoFileName}`)">
        <div class="lower-title">רוצה להוסיף את הפרטים שלו?</div>
      </div>

      <div class="title-container" v-else>
        <span class="title">
          <template v-if="isCard">כרטיס <span :class="{ bold: hasSource }">{{formattedSource}}</span>!</template>
          <template v-else-if="!isCard">מצאתי {{question.billingAmount ? 'חיוב' : 'זיכוי' }}
            <span :class="{ bold: hasSource }">מ{{formattedSource}} </span>
            <span v-if="formattedAccount"><br> (בחשבון {{formattedAccount}})</span>
          </template>
        </span>
        <div class="amount" :class="[amountSize]">
          {{ amount }}
          <span class="nis-currency">₪</span>
        </div>
        <div class="description">
          <div>{{ question.businessName }}</div>
          <div>{{ normalizedBillingDate }}</div>
        </div>
        <div v-if="isCard" class="lower-title">בשביל שהתזרים יהיה מדויק, צריך את הפירוט שלו, רוצה להוסיף את הפרטים?</div>
      </div>

    </div>

    <div v-if="!shouldAddSource" class="question-actions">
      <template v-if="isCard">
        <riseup-button class="button-container" :variant="'primary'" :title="'כן! אני רוצה להוסיף'"
                       :action="() => setAddCreds()"/>
        <riseup-button class="button-container" :variant="'secondary'" :title="'אני לא רוצה להוסיף'"
                       :action="() => setInputAndTrack(papaInputTypes.approved)"/>
      </template>

      <template v-else-if="question.billingAmount">
        <riseup-button class="button-container" :variant="'primary'" :title="'זה כרטיס אשראי'" :subtitle="'אני רוצה להוסיף אותו'"
                     :action="() => setAddCreds()"/>
        <riseup-button class="button-container" :variant="'secondary'" :title="'זה כרטיס אשראי'"  :subtitle="'אני לא רוצה להוסיף אותו'"
                     :action="() => setInputAndTrack(papaInputTypes.approved)"/>
        <riseup-button class="button-container" :variant="'secondary-black'" :title="'זה החזר הלוואה'"
                     :action="() => setInputAndTrack(papaInputTypes.loan)"/>
      </template>

      <template v-else>
        <riseup-button class="button-container" :variant="'primary'" :title="'זה כרטיס אשראי'" :subtitle="'אני רוצה להוסיף אותו'"
                       :action="() => setAddCreds()"/>
        <riseup-button class="button-container" :variant="'secondary'" :title="'זה כרטיס אשראי'"  :subtitle="'אני לא רוצה להוסיף אותו'"
                       :action="() => setInputAndTrack(papaInputTypes.approved)"/>
        <riseup-button class="button-container" :variant="'secondary-black'" :title="'זה זיכוי אשראי'"
                     :action="() => setInputAndTrack(papaInputTypes.clearing)"/>
      </template>
    </div>
    <div v-else>
      <source-selection :disabled="false" :selectionText="'של איזו חברה הכרטיס הזה?'" :sources="cardSources"
                        :sourceType="'card'" :scrollable="false" :onlyExpanded="true" :addSource="addCardSource"/>
    </div>
    <card-bug-report class="bug-report-wrapper" :action="() => setInputAndTrack(papaInputTypes.bug)"/>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import _ from 'lodash';
import BaseUI from 'base-ui';
import moneyUtils from '../../../../../../../utils/money';
import dateUtils from '../../../../../../../utils/dates';
import CardButton from '../../../../../../../base-components/card-parts/CardButton.vue';
import CardBugReport from '../../../../../../../base-components/card-parts/CardBugReport.vue';
import CardInputResponse from '../../../../../../../base-components/card-parts/CardInputResponse.vue';
import sourceToLogs from '../../../../../../../utils/sourcesToLogo';
import SourceSelection from '../../../../../../../base-components/credentials/credentials-selection-card/SourceSelection.vue';
import Segment from '../../../../../../../Segment';
import SourceFields from '../../../../../../../mixins/SourceFields.vue';

export default {
  name: 'PapasMergeCard',
  mixins: [SourceFields],
  components: {
    CardBugReport,
    CardButton,
    CardInputResponse,
    SourceSelection,
    RiseupButton: BaseUI.RiseupButton,
  },
  props: ['question'],
  data() {
    return {
      isCard: false,
      addCreds: false,
    };
  },
  async created() {
    this.isCard = !!this.cardNumber;
  },
  computed: {
    ...mapState('mergeInvestigationQuestions', ['papaInputTypes']),
    cardNumber() {
      const cardNumberMatch = this.question.businessName.match(/\d+/);
      return cardNumberMatch && cardNumberMatch[0].slice(-4);
    },
    knownCardNumber() {
      return !!this.cardNumber;
    },
    cardSource() {
      return this.question.cardSource;
    },
    logoFileName() {
      const logoName = {
        'ויזה כאל': 'cal',
        'ישראכארט': 'isracard', // eslint-disable-line
        'אמריקן אקספרס': 'americanexpress',
        'לאומי קארד': 'leumicard',
      }[this.question.cardSource];
      return sourceToLogs[logoName];
    },
    formattedCardNumber() {
      return `${this.cardNumber} ********`;
    },
    hasSource() {
      return !!(this.question.cardSource && _.find(this.allSourceFields, source => source.sourceName === this.question.cardSource));
    },
    formattedSource() {
      const cardPrefix = this.isCard ? 'אשראי' : 'חב\' אשראי';
      return this.hasSource ? _.find(this.allSourceFields, source =>
        source.sourceName === this.question.cardSource).displayName : cardPrefix;
    },
    formattedAccount() {
      return this.question.account;
    },
    cardSources() {
      return _.filter(this.allSourceFields, source => source.type === 'card' && !source.hidden);
    },
    amount() {
      const amount = this.question.billingAmount ? this.question.billingAmount : this.question.incomeAmount;
      return moneyUtils.formatAmountWithFraction(amount);
    },
    amountSize() {
      if (this.amount.length > 7) {
        return 'long';
      } else if (this.amount.length > 6) {
        return 'medium';
      }
      return '';
    },
    normalizedBillingDate() {
      return dateUtils.normalizeDate(this.question.billingDate);
    },
    shouldAddSource() {
      return this.addCreds && !this.hasSource;
    },
  },
  methods: {
    ...mapActions('mergeInvestigationQuestions', ['setPapaQuestionInput', 'setPapaSource']),
    setAddCreds() {
      this.addCreds = true;
      if (!this.shouldAddSource) {
        this.setInputAndTrack(this.papaInputTypes.addCreds);
      }
    },
    addCardSource({ source }) {
      Segment.trackUserInteraction('PapasMergeCard', { source, businessName: this.question.businessName });
      this.setInputAndTrack(this.papaInputTypes.addCreds);
      this.setPapaSource({ transactionId: this.question.transactionId, cardSource: source.sourceName });
    },
    setInputAndTrack(input) {
      Segment.trackUserInteraction('PapasInvestigationClicked', {
        question: {
          amount: this.amount,
          transactionId: this.question.transactionId,
          businessName: this.question.businessName,
          billingDate: this.question.billingDate,
        },
        input,
      });
      this.setPapaQuestionInput({ transactionId: this.question.transactionId, input });
    },
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../../../../../../node_modules/base-ui/src/scss/riseup-colors';
  @import '../../../../../../../scss/mixins';

  .papas-merge-card {
    @include card;
    padding: 16px 24px;

    .questions-details {
      margin-bottom: 16px;

      .title-container {
        line-height: 1;
        .icon {
          position: relative;
          bottom: -1px;
          margin-left: 5px;
        }
        .title {
          font-size: 20px;
          line-height: 1.1em;
        }
        .lower-title {
          font-size: 20px;
          margin: 15px 0 0;
          line-height: 1.1em;
        }
        .account {
          font-family: SimplerProBold;
          font-size: 35px;
          margin: 20px 0 0;
        }
        .logo {
          width: 112px;
          margin: 4px 0 0;
        }
        .bold {
          font-weight: bold;
        }
      }

      .amount {
        line-height: 1;
        font-family: SimplerProBold;
        font-size: 50px;
        margin: 12px 0;
        margin-top: 20px;

        .nis-currency {
          font-size: 44px;
        }

        &.medium {
          font-size: 50px;

          .nis-currency {
            font-size: 40px;
          }
        }

        &.long {
          font-size: 42px;

          .nis-currency {
            font-size: 30px;
          }
        }
      }

      .description {
        line-height: 1.2;
        font-size: 16px;
        color: $riseup_gray_2;
      }
    }

    .question-actions {
      .button-container {
        margin-bottom: 16px;
      }
    }
  .bug-report-wrapper {
    margin-top: 8px;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/responsive-pages/authenticated/navigation-pages/cashflow-app/investigations/merge-investigation-in-app/PapasMergeCard.vue